import { z } from 'zod';
import { PAGINATION_PAGE_SIZE_LARGE } from '~/constants';

import { INCOTERM_CODES } from '~/constants/incoterms';

import { zodAddressSchema } from '@vestigas/data';

import {
  EmailSchema,
  parseFromInput,
  PhoneNumberSchema,
  toSnakeCase,
} from '@vestigas/utils';
import { i18n } from '~/utils';

import {
  CLEARABLE_CONTRACT_DATE_FIELDS,
  CONTACT_PARTIES,
  CONTACT_ROLES,
  CONTRACT_ACTIONS,
  CONTRACT_CONDITION_KINDS,
  CONTRACT_CONDITION_VALUE_TYPES,
  CONTRACT_DATA_SOURCE,
  CONTRACT_ITEM_WARNING_CODES,
  CONTRACT_STATUS,
  MAX_PRICE_SCALE_STEPS,
} from './constants';

/**
 * Shared validation schemas for contract-related API calls.
 */

export const zodContractStatusSchema = z.preprocess(
  (value) => (typeof value === 'string' ? toSnakeCase(value.trim()) : value),
  z.enum(CONTRACT_STATUS),
);

export const zodContractDataSourceSchema = z.enum(CONTRACT_DATA_SOURCE);

// Contact role enum for contract contacts
export const zodContactRoleSchema = z.preprocess(
  (value) => (typeof value === 'string' ? toSnakeCase(value.trim()) : value),
  z.enum(CONTACT_ROLES),
);

// Contact party enum for contract contacts
export const zodContactPartySchema = z.preprocess(
  (value) => (typeof value === 'string' ? toSnakeCase(value.trim()) : value),
  z.enum(CONTACT_PARTIES),
);

// ExternalPartyInfo schema for buyerInfo and sellerInfo
export const zodExternalPartyInfoSchema = z.object({
  address: zodAddressSchema.nullish(),
  contactPerson: z.string().nullish(),
  email: EmailSchema.nullish(),
  name: z.string().trim().max(255).nullish(),
  phone: z.string().nullish(),
  taxId: z.string().nullish(),
});

// Contract contact schema (contacts array items)
// It's included in the schema for data integrity but should not be displayed as an input.
export const zodContractContactSchema = z.object({
  email: z.union([EmailSchema, z.literal('')]).nullish(),
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  name: z.string().trim().nullish(), // Backend-managed, not displayed in form
  party: zodContactPartySchema,
  phone: z.union([PhoneNumberSchema, z.literal('')]).nullish(),
  role: zodContactRoleSchema,
});

// Payment term schema for contract payment terms (aligned with invoice PaymentTerms)
export const zodContractPaymentTermSchema = z.object({
  actualAmount: z.union([z.number(), z.string()]).nullish(),
  basisAmount: z.union([z.number(), z.string()]).nullish(),
  dueAmount: z.union([z.number(), z.string()]).nullish(),
  dueDate: z
    .union([z.iso.date(), z.literal('')])
    .nullish()
    .transform((value) => (value ? value : null)),
  duePeriod: z.union([z.number(), z.string()]).nullish(),
  duePeriodUnit: z.enum(['DAY', 'WEE', 'MON', 'ANN']).nullish(), // UN/CEFACT units day, week, month, year
  percentage: z
    .preprocess((value) => {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      if (typeof value === 'number') {
        return value;
      }

      if (typeof value === 'string') {
        return parseFromInput(value);
      }

      return value;
    }, z.number().min(0).max(100))
    .nullish()
    .default(0),
});

// Price rule schema for contract items
export const zodPriceRuleSchema = z.object({
  currency: z.string().default('EUR'),
  pricePerUnit: z.union([z.number(), z.string()]).nullish(),
  validFrom: z.iso.date().nullish(),
  validUntil: z.iso.date().nullish(),
});

export const zodContractConditionKindSchema = z.enum(CONTRACT_CONDITION_KINDS);

const zodContractConditionValueTypeSchema = z.enum(
  CONTRACT_CONDITION_VALUE_TYPES,
);

// One boundary of a validity window. Both boundaries carry a year (one-off
// period) or neither does (recurs every year, may cross the year end).
const zodContractConditionDateSchema = z.object({
  day: z.number().int().min(1).max(31),
  month: z.number().int().min(1).max(12),
  year: z.number().int().min(1900).max(9999).nullish(),
});

// Period in which a condition is in force, boundaries included
const zodContractConditionWindowSchema = z.object({
  end: zodContractConditionDateSchema,
  start: zodContractConditionDateSchema,
});

// Quantities (counted in single item units) a condition applies to: from the
// lower bound (included) up to the upper bound (excluded)
const zodContractConditionQuantityRangeSchema = z.object({
  fromQuantity: z.string().nullish(),
  underQuantity: z.string().nullish(),
});

// Discount or surcharge on a contract item (ContractConditionResponse)
export const zodContractConditionSchema = z.object({
  id: z.uuid(),
  kind: zodContractConditionKindSchema,
  name: z.string().nullish(),
  quantityRange: zodContractConditionQuantityRangeSchema.nullish(),
  unit: z.string().nullish(),
  validityWindow: zodContractConditionWindowSchema.nullish(),
  value: z.string(),
  valueType: zodContractConditionValueTypeSchema,
});

// One step of an item's price scale (ContractPriceScaleStepResponse)
export const zodContractPriceScaleStepSchema = z.object({
  fromQuantity: z.string(),
  price: z.string(),
});

// Circumstance of a contract item worth attention; never blocks a save
const zodContractItemWarningSchema = z.object({
  code: z.enum(CONTRACT_ITEM_WARNING_CODES),
  conditionId: z.uuid().nullish(),
});

// Contract item schema (full item with all fields from API response - ContractItemResponse)
export const zodContractItemSchema = z.object({
  buyerArticleId: z.string().trim().nullish(),
  conditions: z.array(zodContractConditionSchema).nullish(),
  contractId: z.uuid(),
  createdOn: z.iso.datetime({ offset: true }),
  currency: z.string().trim().default('EUR'),
  deactivatedOn: z.iso.datetime({ offset: true }).nullish(),
  dlnUnitType: z.string().trim().nullish(),
  // Null on items imported without a readable price; such an item is incomplete.
  grossPrice: z.string().trim().nullish(),
  grossPricePerQuantity: z.coerce.number().int().nullish(),
  id: z.uuid(),
  incoterm: z.string().trim().nullish(),
  isActive: z.boolean().default(true),
  manufacturer: z.string().trim().nullish(),
  modifiedOn: z.iso.datetime({ offset: true }),
  notes: z.string().trim().nullish(),
  overDeliveryWarning: z.boolean().default(false),
  position: z.coerce.number().int().min(1),
  priceScale: z.array(zodContractPriceScaleStepSchema).nullish(),
  productDescription: z.string().trim().nullish(),
  productName: z.string().trim(),
  quantityCalledOff: z.string().trim(),
  quantityDelivered: z.string().trim().default('0'),
  quantityOrdered: z.string().trim().nullish(),
  quantityPending: z.string().trim().default('0'),
  sellerArticleId: z.string().trim().nullish(),
  totalPrice: z.string().trim().nullable(),
  unitMismatchWarning: z.string().trim().nullish(),
  unitType: z.string().trim(),
  warnings: z.array(zodContractItemWarningSchema).default([]),
});

// Lightweight company reference nested in contract responses (id, name, logo).
// Replaces the deprecated flat buyerCompanyId/buyerCompanyName (and seller
// equivalents) pair, mirroring the supplier object's id/name/logoUrl shape.
const zodContractCompanyRefSchema = z.object({
  id: z.uuid(),
  logoUrl: z.url().nullish(),
  name: z.string().nullish(),
});

// Base contract fields shared across list items and full contract responses
const zodContractBaseSchema = z.object({
  accountingReferences: z
    .array(
      z.object({
        id: z.uuid(),
        isActive: z.boolean(),
        name: z.string(),
        type: z.string(),
      }),
    )
    .nullable(),
  // An empty string when the contract has no customer contract number.
  buyerAssignedId: z.string().default(''),
  buyerCompany: zodContractCompanyRefSchema.nullish(),
  buyerCompanyId: z.uuid(), // Deprecated: use buyerCompany.id instead.
  buyerCompanyName: z.string().nullish(), // Deprecated: use buyerCompany.name instead.
  contractDate: z.iso.date().nullable(),
  contractName: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdOn: z.iso.datetime({ offset: true }),
  dataSource: zodContractDataSourceSchema.nullable(),
  id: z.uuid(),
  modifiedOn: z.iso.datetime({ offset: true }),
  sellerAssignedId: z.string().nullable(),
  sellerCompany: zodContractCompanyRefSchema.nullish(),
  // Null on shell drafts: a contract created on entering the create form has
  // no seller until the user picks one. Deprecated: use sellerCompany.id instead.
  sellerCompanyId: z.uuid().nullish(),
  sellerCompanyName: z.string().nullish(), // Deprecated: use sellerCompany.name instead.
  sites: z
    .array(
      z.object({
        id: z.uuid(),
        isActive: z.boolean(),
        name: z.string(),
        type: z.string(),
      }),
    )
    .nullable(),
  status: z.enum(CONTRACT_STATUS),
  validFrom: z.iso.date().nullable(),
  validUntil: z.iso.date().nullable(),
});

// Contract list item schema (used in list responses)
export const zodContractListItemSchema = zodContractBaseSchema.extend({
  additionalData: z.record(z.string(), z.json()).nullish(),
  itemCount: z.number().int().nonnegative().default(0).nullable(),
});

export const zodDeliveryAddressSchema = z.object({
  additionalInfo: z.string().nullish(),
  buildingNumber: z.string().trim().nullish(),
  city: z.string().trim().nullish(),
  country: z.string().trim().length(2).nullish(),
  lineOne: z.string().nullish(),
  postCode: z.string().trim().nullish(),
  streetName: z.string().trim().nullish(),
});
// .refine(
//   (data) =>
//     Boolean(
//       data.additionalInfo ||
//         data.buildingNumber ||
//         data.city ||
//         data.country ||
//         data.lineOne ||
//         data.postCode ||
//         data.streetName,
//     ),
//   {
//     message: i18n.t('form.error.fillInAtLeastOneAddressField', {
//       ns: 'callOff',
//     }),
//     path: ['streetName'],
//   },
// );

export const zodContractAttachmentSchema = z.object({
  fileId: z.uuid(),
  fileLink: z.url().optional(), // Only included in the response, not in the request
  fileMime: z.string().trim().min(1),
  fileName: z.string().trim().min(1),
});

// Request payload schema for creating/validating a contract attachment
export const zodContractAttachmentPayloadSchema =
  zodContractAttachmentSchema.pick({
    fileMime: true,
    fileName: true,
  });

// Full contract schema (includes items array - ContractResponse)
export const zodContractSchema = zodContractBaseSchema.extend({
  accountingReferences: z
    .array(
      z.object({
        id: z.uuid(),
        isActive: z.boolean(),
        name: z.string(),
        type: z.string(),
      }),
    )
    .nullish(),
  additionalData: z.record(z.string(), z.json()).nullish(),
  attachments: z.array(zodContractAttachmentSchema).nullish(),
  availabilityDate: z.iso.date().nullish(),
  companyAccountId: z.uuid(),
  contacts: z.array(zodContractContactSchema).nullish(),
  contractName: z.string().nullish(),
  createdBy: z.uuid().nullish(),
  createdByName: z.string().nullish(),
  currency: z.string(),
  dataSource: zodContractDataSourceSchema.nullish(),
  deliveryAddresses: z.array(zodDeliveryAddressSchema).nullish(),
  deliveryTerms: z.string().nullish(),
  incoterm: z.string().nullish(),
  // Incomplete items of the whole contract; null in the response of a write.
  invalidItemsTotalCount: z.number().int().nonnegative().nullish(),
  // Absent when the contract is fetched with `return_items=false` (large
  // contracts; items then come from the paginated items endpoint) and empty
  // on shell drafts that have no items yet.
  items: z.array(zodContractItemSchema).nullish(),
  keywords: z.string().nullish(),
  modifiedBy: z.uuid().nullish(),
  modifiedByName: z.string().nullish(),
  notes: z.string().nullish(),
  paymentTerms: z.array(zodContractPaymentTermSchema).nullish(),
  sites: z
    .array(
      z.object({
        address: zodAddressSchema.nullish(),
        id: z.uuid(),
        isActive: z.boolean(),
        name: z.string(),
        type: z.string(),
      }),
    )
    .nullish(),
  totalPrice: z.string().trim().nullable(), // Decimal API string. Sum of all item total prices.
});

// Contract response schema (returned from create/update operations)
export const zodContractResponseSchema = zodContractSchema;

// Paginated contracts list response schema
export const zodContractsListResponseSchema = z.object({
  data: z.array(zodContractListItemSchema),
  hasNextPage: z.boolean(),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative().nullable(),
});

// Paginated full contracts, items included (`view=extended`)
export const zodContractsDetailsListResponseSchema =
  zodContractsListResponseSchema.extend({
    data: z.array(zodContractSchema),
  });

// Query parameters shared by the paginated per-version history endpoints
export const zodContractVersionPageQueryParamsSchema = z.object({
  limit: z.number().int().min(1).max(500).default(PAGINATION_PAGE_SIZE_LARGE),
  offset: z.number().int().min(0).default(0),
});

// Pagination envelope shared by the per-version history endpoints; each
// endpoint extends it with its own `data` records.
export const zodContractVersionPageBaseSchema = z.object({
  hasNextPage: z.boolean(),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
});

// Base payload schema with shared fields for contract items (all optional/nullish)
const zodNonNegativeDecimalSchema = z.union([
  z.number().min(0),
  z
    .string()
    .trim()
    .refine(
      (value) =>
        value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0,
    ),
]);

export const zodPositiveDecimalSchema = z.union([
  z.number().positive(),
  z
    .string()
    .trim()
    .refine(
      (value) =>
        value !== '' && Number.isFinite(Number(value)) && Number(value) > 0,
    ),
]);

const zodContractConditionQuantityRangePayloadSchema = z.object({
  fromQuantity: zodPositiveDecimalSchema.nullish(),
  underQuantity: zodPositiveDecimalSchema.nullish(),
});

// Request payload for a condition. A new condition is sent without `id`; an
// edited one carries the id the server assigned, since a saved list replaces
// the stored one. The validity window, quantity range and unit are replaced as
// sent, so omitting one removes it.
export const zodContractConditionPayloadSchema = z.object({
  id: z.uuid().nullish(),
  kind: zodContractConditionKindSchema,
  name: z.string().trim().max(255).nullish(),
  quantityRange: zodContractConditionQuantityRangePayloadSchema.nullish(),
  unit: z.string().trim().nullish(),
  validityWindow: zodContractConditionWindowSchema.nullish(),
  value: zodPositiveDecimalSchema,
  valueType: zodContractConditionValueTypeSchema,
});

const zodContractPriceScaleStepPayloadSchema = z.object({
  fromQuantity: zodPositiveDecimalSchema,
  price: zodNonNegativeDecimalSchema,
});

const zodContractItemPayloadBaseSchema = z.object({
  buyerArticleId: z.string().trim().nullish(),
  conditions: z.array(zodContractConditionPayloadSchema).max(50).nullish(),
  grossPrice: zodNonNegativeDecimalSchema,
  incoterm: z.enum(INCOTERM_CODES).nullish(),
  manufacturer: z.string().trim().nullish(),
  notes: z.string().trim().nullish(),
  position: z.coerce.number().int(),
  priceScale: z
    .array(zodContractPriceScaleStepPayloadSchema)
    .max(MAX_PRICE_SCALE_STEPS)
    .nullish(),
  productDescription: z.string().trim().nullish(),
  productName: z.string().trim().min(1).max(500),
  quantityOrdered: zodNonNegativeDecimalSchema.nullish(),
  sellerArticleId: z.string().trim().nullish(),
  unitType: z.string().trim(),
});

// Request payload schema for creating a contract item
export const zodCreateContractItemPayloadSchema =
  zodContractItemPayloadBaseSchema.extend({
    grossPricePerQuantity: z.number().int().nullish(),
  });

// Request payload schema for updating a contract item (partial update - all fields nullable)
export const zodUpdateContractItemPayloadSchema =
  zodContractItemPayloadBaseSchema.partial().extend({
    grossPricePerQuantity: z.number().int().min(1).nullish(),
  });

// Cleared date inputs yield '' in form state; it is mapped to null before sending.
const zodClearableDateSchema = z.union([z.iso.date(), z.literal('')]).nullish();

// Base payload schema with shared optional fields for create/update contract
export const zodContractPayloadBaseSchema = z.object({
  accountingReferenceIds: z.array(z.uuid()).nullish(),
  accountingReferenceSyncKeys: z.array(z.string()).nullish(),
  additionalData: z.looseObject({}).nullish(),
  allowedDeliverySiteIds: z.array(z.uuid()).nullish(),
  allowedDeliverySiteSyncKeys: z.array(z.string()).nullish(),
  availabilityDate: zodClearableDateSchema,
  // Optional: a whitespace-only value is trimmed to '', which the API stores as NULL.
  buyerAssignedId: z.string().trim().nullish(),
  buyerCompanyId: z.uuid({
    message: i18n.t(($) => $.form.field.buyerCompanyId.errorIsRequired, {
      ns: 'contract',
    }),
  }),
  contacts: z.array(zodContractContactSchema).nullish(),
  contractDate: zodClearableDateSchema,
  contractName: z.string().trim().nullish(),
  currency: z
    .string()
    .max(3, {
      message: i18n.t(($) => $.form.field.currency.invalidCurrencyCode, {
        ns: 'contract',
      }),
    })
    .nullish()
    .default('EUR'),
  deliveryAddresses: z.array(zodDeliveryAddressSchema).nullish(),
  deliveryTerms: z.string().nullish(),
  incoterm: z.string().trim().nullish(),
  notes: z.string().trim().nullish(),
  paymentTerms: z.array(zodContractPaymentTermSchema).nullish(),
  sellerAssignedId: z.string().trim().nullish(),
  sellerCompanyId: z.uuid({
    message: i18n.t(($) => $.form.field.sellerCompanyId.errorIsRequired, {
      ns: 'contract',
    }),
  }),
  status: zodContractStatusSchema.default('draft'),
  validFrom: zodClearableDateSchema,
  validUntil: zodClearableDateSchema,
});

/** The API clears a contract date only when it receives an explicit `null`. */
export const mapEmptyContractDatesToNull = <
  T extends Partial<
    Record<
      (typeof CLEARABLE_CONTRACT_DATE_FIELDS)[number],
      string | null | undefined
    >
  >,
>(
  data: T,
): T => {
  const clearedDates = Object.fromEntries(
    CLEARABLE_CONTRACT_DATE_FIELDS.filter((field) => data[field] === '').map(
      (field) => [field, null],
    ),
  );

  return { ...data, ...clearedDates };
};

// Contract item response schema (returned from create/update operations - same as ContractItemResponse)
export const zodContractItemResponseSchema = zodContractItemSchema;

// Contract public view response schema (without pricing info, for supplier view)
export const zodContractPublicViewSchema = z.object({
  buyerAssignedId: z.string().nullish(),
  buyerCompanyName: z.string().nullish(),
  contractDate: z.iso.date(),
  deliveryTerms: z.string().nullish(),
  id: z.uuid(),
  notes: z.string().nullish(),
  sellerAssignedId: z.string().nullish(),
  sellerCompanyName: z.string().nullish(),
  status: zodContractStatusSchema,
  validFrom: z.iso.date(),
  validUntil: z.iso.date(),
});

// Request payload schema for confirming a contract
export const zodConfirmContractPayloadSchema = z.object({
  notes: z.string().nullish(),
});

// Contract action enum schema for status transitions (shared across actions hooks)
export const zodContractActionSchema = z.preprocess(
  (value) => (typeof value === 'string' ? toSnakeCase(value.trim()) : value),
  z.enum(CONTRACT_ACTIONS),
);

// Action metadata schema (shared across actions hooks)
export const zodContractActionMetadataSchema = z.object({
  action: zodContractActionSchema,
  description: z.string().nullish(),
  label: z.string().nullish(),
  requiresConfirmation: z.boolean().nullish(),
});
